import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";

import { useGitLabRepoTree } from "../../../../hooks/useGitLabRepoTree";
import {
  ACCENT,
  FONT,
  HAIRLINE,
  HOVER,
  ICON,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  SURFACE_MUTED,
  WEIGHT,
} from "../../../../theme/tokens";

const TABLE_GRID_TEMPLATE = "minmax(0, 1.2fr) minmax(200px, 0.9fr) 116px";

const CODE_EXT = new Set([
  "py",
  "js",
  "jsx",
  "ts",
  "tsx",
  "sh",
  "bash",
  "go",
  "c",
  "cc",
  "cpp",
  "h",
  "hpp",
  "java",
  "rs",
  "rb",
  "r",
]);
const DATA_EXT = new Set(["json", "yaml", "yml", "toml", "ini", "cfg", "env"]);
const DOC_EXT = new Set(["md", "markdown", "txt", "rst", "log"]);
const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp", "ico"]);

// Choose a monochrome glyph by file type. Color stays neutral by design — the
// icon shape (not color) carries the scannability, keeping the one-accent rule.
function getFileGlyph(name) {
  const lower = (name || "").toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop() : "";

  if (lower.startsWith("dockerfile")) return CodeOutlinedIcon;
  if (lower.endsWith(".lock") || ext === "lock") return LockOutlinedIcon;
  if (CODE_EXT.has(ext)) return CodeOutlinedIcon;
  if (DATA_EXT.has(ext)) return DataObjectOutlinedIcon;
  if (DOC_EXT.has(ext)) return ArticleOutlinedIcon;
  if (IMAGE_EXT.has(ext)) return ImageOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
}

function formatRelativeTime(isoString) {
  if (!isoString) return "";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function getParentPath(path) {
  if (!path) return "";
  const segments = path.split("/").filter(Boolean);
  return segments.slice(0, -1).join("/");
}

function buildBreadcrumbs(projectName, currentPath) {
  const segments = currentPath ? currentPath.split("/").filter(Boolean) : [];
  const crumbs = [{ label: projectName, path: "" }];

  segments.forEach((segment, index) => {
    crumbs.push({
      label: segment,
      path: segments.slice(0, index + 1).join("/"),
    });
  });

  return crumbs;
}

function ExplorerRow({
  item,
  onOpenDirectory,
  onOpenFile,
  commitInfo,
  isParentRow = false,
}) {
  const isDir = isParentRow || item.type === "tree";
  const message = isParentRow
    ? "Back to the parent directory"
    : commitInfo?.message;
  const committedDate = isParentRow ? "" : commitInfo?.committedDate;
  const rowLabel = isParentRow ? "Back" : item.name;

  let TileIcon = FolderRoundedIcon;
  if (isParentRow) TileIcon = SubdirectoryArrowLeftIcon;
  else if (!isDir) TileIcon = getFileGlyph(item.name);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: TABLE_GRID_TEMPLATE,
        alignItems: "center",
        px: 1.5,
        py: 0.85,
        borderRadius: `${RADIUS.sm}px`,
        cursor: item.type === "blob" || isDir ? "pointer" : "default",
        transition: "background-color 0.14s ease",
        "&:hover": { backgroundColor: HOVER },
        "&:hover .file-tree-name": { color: ACCENT },
        "&:hover .file-tree-chevron": {
          opacity: isDir && !isParentRow ? 1 : 0,
        },
      }}
      onClick={() => {
        if (isDir) {
          onOpenDirectory(item.path);
        } else if (item.type === "blob") {
          onOpenFile(item);
        }
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}
      >
        <TileIcon
          sx={{
            flexShrink: 0,
            fontSize: 20,
            color: isDir && !isParentRow ? ACCENT : ICON,
          }}
        />
        <Typography
          className="file-tree-name"
          sx={{
            fontSize: FONT.md,
            fontWeight: isDir ? WEIGHT.medium : WEIGHT.regular,
            color: isParentRow ? INK_SECONDARY : INK,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            transition: "color 0.14s ease",
          }}
        >
          {rowLabel}
        </Typography>
        <ChevronRightIcon
          className="file-tree-chevron"
          sx={{
            fontSize: 16,
            color: ICON,
            opacity: 0,
            flexShrink: 0,
            transition: "opacity 0.14s ease",
          }}
        />
      </Box>

      <Tooltip
        title={message || ""}
        placement="top"
        disableHoverListener={!message}
      >
        <Typography
          sx={{
            fontSize: FONT.sm,
            color: INK_SECONDARY,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            pr: 2,
          }}
        >
          {message || "—"}
        </Typography>
      </Tooltip>

      <Typography
        sx={{
          fontSize: FONT.xs,
          color: INK_MUTED,
          justifySelf: "end",
          pr: 1.5,
          whiteSpace: "nowrap",
        }}
      >
        {committedDate ? formatRelativeTime(committedDate) : "—"}
      </Typography>
    </Box>
  );
}

ExplorerRow.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    type: PropTypes.string,
  }).isRequired,
  onOpenDirectory: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func.isRequired,
  commitInfo: PropTypes.shape({
    committedDate: PropTypes.string,
    message: PropTypes.string,
  }),
  isParentRow: PropTypes.bool,
};

export default function FileTree({
  projectId,
  projectName,
  onOpenFile,
  showHeading = true,
}) {
  const [currentPath, setCurrentPath] = useState("");
  const { items, commitInfo, loading, error } = useGitLabRepoTree(
    projectId,
    currentPath,
  );

  useEffect(() => {
    setCurrentPath("");
  }, [projectId]);

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(projectName, currentPath),
    [currentPath, projectName],
  );
  const breadcrumbCopyText = useMemo(
    () => breadcrumbs.map((crumb) => crumb.label).join("/"),
    [breadcrumbs],
  );

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.type !== b.type) return a.type === "tree" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [items]);

  const { folderCount, fileCount } = useMemo(() => {
    let folders = 0;
    let files = 0;
    for (const item of sortedItems) {
      if (item.type === "tree") folders += 1;
      else if (item.type === "blob") files += 1;
    }
    return { folderCount: folders, fileCount: files };
  }, [sortedItems]);

  const handleOpenDirectory = (path) => {
    setCurrentPath(path);
  };

  const handleNavigateToCrumb = (path) => {
    setCurrentPath(path);
  };

  const handleCopyPath = () => {
    navigator.clipboard?.writeText(breadcrumbCopyText);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  const summaryParts = [];
  if (folderCount > 0) {
    summaryParts.push(`${folderCount} folder${folderCount !== 1 ? "s" : ""}`);
  }
  if (fileCount > 0) {
    summaryParts.push(`${fileCount} file${fileCount !== 1 ? "s" : ""}`);
  }

  return (
    <Box sx={{ mt: 1 }}>
      {showHeading && (
        <Typography
          sx={{
            fontSize: FONT.sm,
            fontWeight: 600,
            color: INK_SECONDARY,
            mb: 1,
          }}
        >
          Files
        </Typography>
      )}

      <Box
        sx={{
          border: `1px solid ${HAIRLINE}`,
          borderRadius: `${RADIUS.md}px`,
          backgroundColor: SURFACE,
          overflow: "hidden",
        }}
      >
        {/* Breadcrumb toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          <HomeRoundedIcon sx={{ fontSize: 16, color: ICON, flexShrink: 0 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: 0.25,
              minWidth: 0,
              flex: 1,
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;

              return (
                <Box
                  key={crumb.path || "__root__"}
                  sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                >
                  {index > 0 && (
                    <ChevronRightIcon
                      sx={{ fontSize: 16, color: INK_MUTED, mx: 0.25 }}
                    />
                  )}

                  {isLast ? (
                    <Typography
                      sx={{
                        fontSize: FONT.sm,
                        fontWeight: 600,
                        color: INK,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {crumb.label}
                    </Typography>
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => handleNavigateToCrumb(crumb.path)}
                      sx={{
                        fontSize: FONT.sm,
                        fontWeight: 500,
                        color: INK_SECONDARY,
                        "&:hover": { color: ACCENT },
                      }}
                    >
                      {crumb.label}
                    </Link>
                  )}
                </Box>
              );
            })}
          </Box>

          {summaryParts.length > 0 && (
            <Typography
              sx={{
                fontSize: FONT.xs,
                color: INK_MUTED,
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: { xs: "none", sm: "block" },
              }}
            >
              {summaryParts.join(" · ")}
            </Typography>
          )}

          <Tooltip title="Copy path">
            <IconButton
              size="small"
              onClick={handleCopyPath}
              sx={{ color: ICON, flexShrink: 0 }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Column header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: TABLE_GRID_TEMPLATE,
            alignItems: "center",
            px: 2,
            py: 1,
            backgroundColor: SURFACE_MUTED,
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          <Typography
            sx={{ fontSize: FONT.xs, fontWeight: 600, color: INK_SECONDARY }}
          >
            Name
          </Typography>
          <Typography
            sx={{ fontSize: FONT.xs, fontWeight: 600, color: INK_SECONDARY }}
          >
            Last commit
          </Typography>
          <Typography
            sx={{
              fontSize: FONT.xs,
              fontWeight: 600,
              color: INK_SECONDARY,
              justifySelf: "end",
              pr: 1.5,
            }}
          >
            Modified
          </Typography>
        </Box>

        {/* Rows */}
        <Box sx={{ p: 0.5 }}>
          {currentPath && (
            <ExplorerRow
              item={{
                name: "..",
                path: getParentPath(currentPath),
                type: "tree",
              }}
              onOpenDirectory={handleOpenDirectory}
              onOpenFile={onOpenFile}
              isParentRow
            />
          )}

          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1.5,
              }}
            >
              <CircularProgress size={14} sx={{ color: ICON }} />
              <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
                Loading directory…
              </Typography>
            </Box>
          ) : sortedItems.length > 0 ? (
            sortedItems.map((item) => (
              <ExplorerRow
                key={item.id}
                item={item}
                commitInfo={commitInfo[item.path]}
                onOpenDirectory={handleOpenDirectory}
                onOpenFile={onOpenFile}
              />
            ))
          ) : (
            <Box sx={{ px: 1.5, py: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
                This directory is empty.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

FileTree.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectName: PropTypes.string.isRequired,
  onOpenFile: PropTypes.func.isRequired,
  showHeading: PropTypes.bool,
};
